import React, { useState, useEffect, useCallback } from "react";
import styles from "./RegisterNew.module.scss";
import EmailValidation from "../../components/UI/EmailValidation/EmailValidation.tsx";
import CodeValidation from "../../components/UI/CodeValidation/CodeValidation.tsx";
import PasswordSetup from "../../components/UI/PasswordSetup/PasswordSetup.tsx";
import { motion } from "framer-motion";
import { RegistrationStep, EmailFormValues, PasswordFormValues } from "../../types/register.ts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { loginWithGoogle } from "../../store/actions/authActions";
import { createPreRegister, resendVerificationCode, clearRegistrationFlow, completeUserRegistration, getSkills } from "../../store/actions";
import LogoEdify from "../../assets/images/Logo-edify-no-bg.png";

interface RegisterNewProps {}

const RegisterNew: React.FC<RegisterNewProps> = () => {
  const [currentStep, setCurrentStep] = useState<RegistrationStep>("email");
  const [email, setEmail] = useState<string>("");
  const [validatedEmail, setValidatedEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [, setRegistrationMethod] = useState<string>("email");
  const [isFromLoginValidation, setIsFromLoginValidation] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const codeValidationData = useSelector((state: any) => state.register?.codeValidationData || null);
  const completeRegistrationData = useSelector((state: any) => state.register?.completeRegistrationData || null);
  const completeRegistrationError = useSelector((state: any) => state.register?.completeRegistrationError || null);
  const skills = useSelector((state: any) => state.register?.skills || []);

  useEffect(() => {
    dispatch(getSkills() as any);
  }, [dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const stepParam = urlParams.get('step');
    const fromLoginValidation = localStorage.getItem('fromLoginValidation');
    const emailFromLogin = localStorage.getItem('EmailRegister');

    if (stepParam === 'code-validation' && fromLoginValidation === 'true' && emailFromLogin) {
      setEmail(emailFromLogin);
      setCurrentStep("code");
      setIsLoading(false);
      setErrorMessage("");
      setIsFromLoginValidation(true);
      localStorage.removeItem('fromLoginValidation');
    }
  }, [location.search]);

  useEffect(() => {

    if (codeValidationData && !email) {
      setCurrentStep("email");
      setEmail("");
      setValidatedEmail("");
      setIsLoading(false);
      setErrorMessage("");
      setRegistrationMethod("email");
      

      dispatch(clearRegistrationFlow() as any);
    }
  }, [codeValidationData, email, dispatch]);


  const handleEmailSubmit = async (values: EmailFormValues): Promise<void> => {
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      if (values.isAlreadyValidated) {
        setEmail(values.email);
        setValidatedEmail(values.email);
        setCurrentStep("password");
        setIsLoading(false);
        return;
      }
      
      if (values.isPreRegistered) {
        await dispatch(resendVerificationCode(values.email) as any);
        setEmail(values.email);
        setCurrentStep("code");
        setIsLoading(false);
        return;
      }
      
      if (values.isNewUser) {
        setEmail(values.email);
        setCurrentStep("code");
        setIsLoading(false);
        return;
      }
      
      await dispatch(createPreRegister(values.email) as any);

      setEmail(values.email);
      setCurrentStep("code");
      setIsLoading(false);
    } catch (error) {
      console.error("Error al enviar código:", error);
      setErrorMessage("Error al enviar el código. Intenta nuevamente.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (codeValidationData) {
      setValidatedEmail(email);
      setCurrentStep("password");
      setIsLoading(false);
    }
  }, [codeValidationData, email]);

  const handleSkipOnboarding = useCallback(() => {
    const defaultSkillName = "Otros";
    const defaultSkill = skills.find((skill: any) => skill.name === defaultSkillName);
    
    const defaultData = {
      accountType: "PERSON", 
      personaForm: {
        name: "Usuario",
        lastName: "Edify", 
        tel: "",
        webSite: ""
      },
      empresaForm: {
        companyName: "Empresa Edify",
        tel: "",
        webSite: ""
      },
      userSkills: defaultSkill ? [{ id: defaultSkill.id, name: defaultSkill.name }] : [],
      workZones: [],
      allCountry: true 
    };
    
    Object.entries(defaultData).forEach(([key, value]) => {
      localStorage.setItem(`${key}New`, JSON.stringify(value));
    });

    navigate("/resumen");
  }, [navigate, skills]);

  useEffect(() => {
    if (completeRegistrationData) {
      setIsLoading(false);
      
      if (completeRegistrationData.skipOnboarding) {
        handleSkipOnboarding();
      } else {
        navigate("/select-type");
      }
    }
  }, [completeRegistrationData, navigate, handleSkipOnboarding]);

  const handleResendCode = async (): Promise<void> => {
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      await dispatch(resendVerificationCode(email) as any);
      
      setErrorMessage("");
      setIsLoading(false);
    } catch (error) {
      console.error("Error al reenviar código:", error);
      setErrorMessage("Error al reenviar el código. Intenta nuevamente.");
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (values: PasswordFormValues): Promise<void> => {
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      await dispatch(completeUserRegistration(values.email, values.password, values.skipOnboarding) as any);
      
      setIsLoading(false);
    } catch (error) {
      console.error("Error al completar registro:", error);
      setErrorMessage("Error al completar el registro. Intenta nuevamente.");
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (idToken: string): Promise<void> => {
    setRegistrationMethod("google");
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      (dispatch as any)(loginWithGoogle(idToken, navigate));
    } catch (error) {
      setErrorMessage("Error al iniciar sesión con Google");
      setIsLoading(false);
    }
  };

  const handleChangeEmail = (): void => {
    setCurrentStep("email");
    setErrorMessage("");
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "email":
        return (
          <EmailValidation
            onEmailSubmit={handleEmailSubmit}
            onGoogleSuccess={handleGoogleSuccess}
            isLoading={isLoading}
            errorMessage={errorMessage}
          />
        );
      case "code":
        return (
          <CodeValidation
            email={email}
            onResendCode={handleResendCode}
            onChangeEmail={handleChangeEmail}
            isLoading={isLoading}
            errorMessage={errorMessage}
            specialMessage={isFromLoginValidation ? "Ya se envió un nuevo código a tu email. Revisa tu bandeja de entrada." : undefined}
          />
        );
      case "password":
        return (
          <PasswordSetup
            email={validatedEmail}
            onPasswordSubmit={handlePasswordSubmit}
            isLoading={isLoading}
            errorMessage={errorMessage || completeRegistrationError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <motion.img
            src={LogoEdify}
            alt="Edify Logo"
            className={styles.logo}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
          <motion.h1 
            className={styles.mainTitle}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Sumate a la comunidad
          </motion.h1>
          <motion.p 
            className={styles.mainSubtitle}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            La plataforma líder para profesionales de la construcción
          </motion.p>
          
        </div>

        <div className={styles.stepContainer}>
          {renderCurrentStep()}
        </div>

        <motion.div 
          className={styles.footer}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className={styles.footerText}>
            ¿Ya tenes una cuenta?{" "}
            <a href="/" className={styles.footerLink}>
              Inicia sesión
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterNew;
